import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DiscountCodeService from 'services/discount-code.service';
import { PartialDiscountCodeType } from 'types/discount-code.types';
import { cleanQueryParams } from 'utils/route-helpers';
import { useNotification } from 'utils/snackbar';
import validate from 'utils/validate';

import { DiscountCodeModal } from './DiscountCodeModal';

const discountCodeService = new DiscountCodeService();

type DiscountCodeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAdd: () => void;
};

const DiscountCodeModalContainer = ({ isOpen, onClose, onAdd }: DiscountCodeModalProps) => {
  const { t } = useTranslation();

  const { showErrorNotification } = useNotification();

  const [name, setName] = useState('');
  const [discountType, setDiscountType] = useState('');
  const [discountAmount, setDiscountAmount] = useState(null);
  const [maxValue, setMaxValue] = useState(1000000);
  const [formErrors, setFormErrors] = useState<any>({});

  const earningNote = t('components.discount-code-modal.earning-note', {
    returnObjects: true,
  }) as string;

  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value;

    setName(name);
    setFormErrors({
      ...formErrors,
      code: null,
    });
  };

  const onDiscountTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const type = event.target.value;

    if (type === 'percentage') {
      setMaxValue(99);
    } else {
      setMaxValue(1000000);
    }
    setDiscountType(type);
    setFormErrors({
      ...formErrors,
      discount_type: null,
    });
  };

  const onDiscountAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const amount = +event.target.value;

    setDiscountAmount(amount);
    setFormErrors({
      ...formErrors,
      discount_amount: null,
    });
  };

  const handleSubmit = async () => {
    const discountData: PartialDiscountCodeType = cleanQueryParams({
      code: name,
      discountAmount,
      discountType,
      status: 'active',
    });

    const validation = validate(discountData, {
      code: {
        presence: {
          allowEmpty: false,
          message: t('components.discount-code-modal.errors.no-name'),
        },
        length: {
          minimum: 5,
          maximum: 20,
          message: t('components.discount-code-modal.errors.invalid-name-length', {
            min: 5,
            max: 20,
          }),
        },
        format: {
          pattern: '^[a-zA-Z0-9]*$',
          flags: 'i',
          message: t('components.discount-code-modal.errors.invalid-name'),
        },
      },
      discountType: {
        presence: {
          allowEmpty: false,
          message: t('components.discount-code-modal.errors.no-type'),
        },
      },
      discountAmount: {
        presence: {
          allowEmpty: false,
          message: t('components.discount-code-modal.errors.no-amount'),
        },
        numericality: {
          greaterThanOrEqualTo: 1,
          lessThanOrEqualTo: maxValue,
          message: t('components.discount-code-modal.errors.wrong-amount', {
            min: 1,
            max: maxValue,
          }),
        },
      },
    });

    if (validation.hasErrors) {
      setFormErrors(validation.errors);

      return;
    }

    try {
      await discountCodeService.create(discountData, {
        silentFail: true,
      });

      setName('');
      setDiscountType('');
      setDiscountAmount(null);
      setFormErrors({});

      onClose();
      onAdd();
    } catch (error) {
      // "Code already used" error
      if (error?.data?.data?.errorCode === 'ERR_DUPLICATE_CODE') {
        setFormErrors({
          code: [t('components.discount-code-modal.errors.used')],
        });

        return;
      }

      // "Reserved code (clash with promo codes)" error
      if (error?.data?.data?.errorCode === 'ERR_RESERVED_CODE') {
        setFormErrors({
          code: [t('components.discount-code-modal.errors.invalid-code')],
        });

        return;
      }

      // General error
      showErrorNotification(error);
    }
  };

  const discountCodeModalProps = {
    isOpen,
    name,
    formErrors,
    discountType,
    maxValue,
    earningNote,
    discountAmount,
    onClose,
    onDiscountAmountChange,
    onDiscountTypeChange,
    onNameChange,
    onSubmit: handleSubmit,
  };

  return <DiscountCodeModal {...discountCodeModalProps} />;
};

export default DiscountCodeModalContainer;
